import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { icons as lucideIcons } from 'lucide-react'
import { useMemo } from 'react'

import {
	preloadGetPointOfSale,
	preloadListProductGroupsForPointOfSale,
	useGetPointOfSale,
	useListProductGroupsForPointOfSale
} from '@/api/react-query'
import { AssetImage } from '@/components/base/asset-image'
import { Badge } from '@/components/base/badge'
import { Container, ContainerHeader } from '@/components/base/container'
import { EmptyState } from '@/components/base/empty-state'
import { IconButton } from '@/components/base/icon-button'
import { SectionRow } from '@/components/base/section-row'
import { Text } from '@/components/base/text'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Photo } from '@/icons/photo'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

const kebabToPascal = (value: string) =>
	value
		.split('-')
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join('')

function ProductGroupGlyph({ name }: { name: string }) {
	const Glyph = lucideIcons[kebabToPascal(name) as keyof typeof lucideIcons]
	if (!Glyph) {
		return <Photo className='text-ui-fg-muted' />
	}
	return <Glyph className='size-5 text-ui-fg-subtle' />
}

export const Route = createFileRoute('/_authenticated/settings/points-of-sale/$pointOfSaleUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit', '/product-menu']}>
			<PointOfSaleDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { pointOfSaleUuid } }) => {
		const [pointOfSale] = await Promise.all([
			preloadGetPointOfSale(context, { path: { pointOfSaleUuid } }),
			preloadListProductGroupsForPointOfSale(context, {
				path: { pointOfSaleUuid },
				query: { limit: 100, offset: 0 }
			})
		])
		return pointOfSale
	},
	staticData: {
		crumb: ({ loaderData, context: { L } }) => loaderData?.name || L.Settings.PointOfSale.Title()
	}
})

function PointOfSaleDetail() {
	const { L } = useI18n()
	const { pointOfSaleUuid } = Route.useParams()

	const navigate = Route.useNavigate()
	const pointOfSale = useGetPointOfSale({ pointOfSaleUuid })
	const productGroups = useListProductGroupsForPointOfSale({ pointOfSaleUuid }, { limit: 100, offset: 0 })

	const salesChannelColumnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				flex: 2
			}
		],
		[L]
	)

	if (!pointOfSale.data) {
		return null
	}

	return (
		<SingleColumnPage hasOutlet>
			<Container>
				<ContainerHeader
					heading={pointOfSale.data.name}
					rightSide={
						<Link from={Route.fullPath} to='./edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
				/>
				<SectionRow
					title={L.Settings.PointOfSale.Column.Group()}
					value={
						pointOfSale.data.group ? (
							<Link
								to='/settings/points-of-sale/groups/$groupUuid'
								params={{ groupUuid: pointOfSale.data.group.uuid }}
							>
								<Badge size='2xsmall'>{pointOfSale.data.group.name}</Badge>
							</Link>
						) : (
							L.Settings.PointOfSale.GroupNone()
						)
					}
				/>
				<SectionRow title={L.Settings.PointOfSale.Column.Identifier()} value={pointOfSale.data.identifier} />
			</Container>

			<Container>
				<ContainerHeader heading={L.Fields.SalesChannel()} />
				<Table
					className='w-full'
					data={pointOfSale.data.salesChannels}
					columnUtilities={{}}
					columnDefs={salesChannelColumnDefs}
					animateRows
					domLayout='autoHeight'
					rowSelection={{
						mode: 'singleRow',
						checkboxes: false,
						enableClickSelection: false,
						enableSelectionWithoutKeys: false
					}}
					suppressCellFocus
					rowToRouterConfig={sc => ({
						to: '/settings/sales-channels/$salesChannelUuid',
						params: { salesChannelUuid: sc.uuid }
					})}
					emptyState={
						<EmptyState
							title='No sales channels'
							description='This point of sale is not connected to any sales channels.'
							className='min-h-[120px]'
						/>
					}
				/>
			</Container>

			<Container>
				<ContainerHeader
					heading={L.Settings.PointOfSale.ProductMenu.Title()}
					rightSide={
						<Link from={Route.fullPath} to='./product-menu'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
				/>
				<Text className='px-6 pb-4 text-sm text-ui-fg-subtle'>
					{L.Settings.PointOfSale.ProductMenu.Description()}
				</Text>
				{productGroups.data && productGroups.data.groups.length > 0 ? (
					<div className='flex flex-col divide-y divide-ui-border-base border-t border-ui-border-base'>
						{productGroups.data.groups.map(group => (
							<div key={group.uuid} className='flex items-center gap-x-3 px-6 py-3'>
								<div className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-ui-border-base bg-ui-bg-component'>
									{group.image ? (
										<AssetImage
											asset={group.image}
											className='size-full'
											preferredVariant='THUMBNAIL'
										/>
									) : group.icon ? (
										<ProductGroupGlyph name={group.icon} />
									) : (
										<Photo className='text-ui-fg-muted' />
									)}
								</div>
								<Text className='flex-1 truncate text-ui-fg-base'>{group.name}</Text>
								{group.isDefault && (
									<Badge color='blue' size='2xsmall'>
										{L.Settings.PointOfSale.ProductMenu.ShownIndividuallyBadge()}
									</Badge>
								)}
							</div>
						))}
					</div>
				) : (
					<EmptyState
						title={L.Settings.PointOfSale.ProductMenu.EmptyTitle()}
						description={L.Settings.PointOfSale.ProductMenu.EmptyDescription()}
						className='min-h-[120px]'
						action={{
							label: L.General.Edit(),
							onClick: () => navigate({ to: './product-menu' })
						}}
					/>
				)}
			</Container>
		</SingleColumnPage>
	)
}
